// ==================== ENUMS ====================

import { Role } from '../role/role';
import { IDepositTransaction } from './Deposit';

export enum PrintAreaType {
  LeftChest = 'left_chest',
  CenterChestSmall = 'center_chest_small',
  CenterChest = 'center_chest',
  FullFront = 'full_front',

  // Bag areas
  BagSmallCenter = 'bag_small_center',
  BagNormalCenter = 'bag_normal_center',
  BagFullCenter = 'bag_full_center',
}

export enum RequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum OrderStatus {
  PENDING = 'pending',
  PRODUCTION_IN_PROGRESS = 'production_in_progress',
  SHIPPING = 'shipping',
  SHIPPED = 'shipped',
  REFUNDED = 'refunded',
  PROCESSED = 'processed',
  CANCELLED = 'cancelled',
}
export enum ShipmentStatus {
  PRE_TRANSIT = 'pre_transit',
  IN_TRANSIT = 'in_transit',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  AVAILABLE_FOR_PICKUP = 'available_for_pickup',
  RETURN_TO_SENDER = 'return_to_sender',
  FAILURE = 'failure',
  CANCELLED = 'cancelled',
  ERROR = 'error',
  UNKNOWN = 'unknown',
}

export enum ProductType {
  TSHIRT = 'tshirt',
  BAG = 'bag',
}

export enum ProductSize {
  // T-shirt sizes
  S = 'S',
  M = 'M',
  L = 'L',
  XL = 'XL',
  XXL = 'XXL',
  XXXL = 'XXXL',

  // Bag sizes
  BAG_WITH_LOCK = 'BAG_WITH_LOCK',
  BAG_WITHOUT_LOCK = 'BAG_WITHOUT_LOCK',
}

export enum CountryCode {
  US = 'us',
  CA = 'ca',
}

export enum SortOption {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum Platform {
  SHOPEE = 'shopee',
  TIKTOK = 'tiktok',
  OTHER = 'other',
}

export enum OrderType {
  HIGH_PRIORITY = 'high_priority',
  NORMAL = 'normal',
}

export enum Side {
  FRONT = 'front',
  BACK = 'back',
}
// ==================== INTERFACES ====================

export interface IUser {
  id: string;
  username: string;
  password?: string; // Excluded in responses
  email: string;
  role: Role;
  name: string;
  deposit: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  orders?: IOrder[];
  refundRequests?: IRefundRequest[];
  sellerDeposit?: ISellerDeposit;
  depositTransactions?: IDepositTransaction[];
}

export interface IPrintArea {
  id: PrintAreaType;
  name: string;
  description?: string;
  price: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IDesign {
  id: string;
  sku?: string;
  designUrl: string;
  seller: string; // User ID
  thumbnailUrl?: string;
  isGeneratingThumbnail?: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  orderDesigns?: IOrderDesign[];
  // Legacy fields for backward compatibility
  design?: any;
  isSelected?: boolean;
  originalFileUrl?: string;
}

export interface IProductTemplateRequest {
  type: ProductType;
  name: string;
  form?: string;
  description?: string;
  color: string;
  size: ProductSize;
  price: number;
  stockQuantity: number;
  frontTemplateUrl?: string | null;
  backTemplateUrl?: string | null;
}

export interface ICreateProductTemplateRequest {
  type: ProductType;
  name: string;
  form: string;
  description?: string;
  color: string;
  colorHexCode: string;
  frontTemplateUrl?: string | null;
  backTemplateUrl?: string | null;
  variants: {
    size: ProductSize;
    price: number;
    stockQuantity: number;
  }[];
}

export interface IProductTemplate {
  id: string;
  type: ProductType;
  name: string;
  form: string;
  description?: string;
  color: string;
  colorHexCode: string;
  frontTemplateUrl?: string | null;
  backTemplateUrl?: string | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  variants: IProductTemplateVariant[];
}

export interface IProductTemplateVariantRequest {
  price: number;
  stockQuantity: number;
}

export interface IProductTemplateVariant {
  id: string;
  price: number;
  size: ProductSize;
  stockQuantity: number;
  productTemplate?: IProductTemplate;
}

export interface IOrderDesignPlacement {
  designId: string;
  design: IDesign;
  side: Side;
  printAreaId: string;
  printArea: PrintAreaType;
  mockupUrl?: string;
  orderDesign: IOrderDesign;
}

export interface IOrderDesign {
  id: string;
  order: IOrder; // Order ID
  productTemplate: IProductTemplate; // ProductTemplate ID
  productTemplateVariant: IProductTemplateVariant;
  quantity: number;
  itemCost: number;
  createdAt: Date;
  updatedAt: Date;
  placements: Array<IOrderDesignPlacement>;
}

export interface IOrderDesignResponse {
  id: string;
  quantity: number;
  itemCost: number;
  design?: IDesign;
  printArea?: IPrintArea;
  productTemplate?: IProductTemplate;
  createdAt: Date;
  updatedAt: Date;
}

export interface ISellerDeposit {
  id: string;
  seller: IUser;
  balance: number;
  createdAt: Date;
  lastUpdatedAt: Date;
}

export interface IRefundRequest {
  id: string;
  order: IOrder; // Order ID
  seller: IUser; // User ID
  reason?: string;
  evidence: string;
  status: RequestStatus;
  requestedAt: Date;
  updatedAt: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
}

export interface IResendRequest {
  id: string;
  seller: IUser; // User ID
  reason?: string;
  status: RequestStatus;
  requestedAt: Date;
  updatedAt: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
}

export interface IOrder {
  id: string;
  orderId: string;
  trackingCode: string;
  trackingReference: string;
  seller: IUser; // User ID
  orderDesigns: IOrderDesign[];
  status: OrderStatus;
  platform: Platform;
  orderType: OrderType;
  totalCost: number; // Computed property
  shippingCost?: number;
  address?: string;
  notes?: string;
  internalNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  lastStatusUpdatedAt: Date;
  refundRequests?: IRefundRequest[];
  depositTransactions?: IDepositTransaction[];
  // Single request properties for convenience
  refundRequest?: IRefundRequest;
  resendRequest?: IResendRequest;
  // Legacy fields for backward compatibility
  sellerId?: string;
  designs?: IOrderItem[];
  orderDesignsResponse?: IOrderDesignResponse[];
}

// ==================== LEGACY INTERFACES (for backward compatibility) ====================

export interface IOrderItem {
  id?: string;
  productTemplateId?: string;
  productTemplateVariantId?: string; // Field used by form for size variants
  itemCost?: number;
  quantity: number;
  placements?: IOrderDesignPlacement[];
}

export interface IOrderItemResponse {
  design?: IDesign;
  id?: string;
  quantity?: number;
  printArea?: IPrintArea;
  productTemplateVariant?: IProductTemplateVariant;
  itemCost?: number;
  mockupUrl?: string;
}

// ==================== CONSTANTS AND MAPS ====================

export const ResendRequestStatusLabelMap = {
  [RequestStatus.APPROVED]: 'Resend Approved',
  [RequestStatus.PENDING]: 'Pending Resend',
  [RequestStatus.REJECTED]: 'Resend Rejected',
};

export const CountryExtraCostMap: Record<CountryCode, number> = {
  [CountryCode.CA]: 1.99,
  [CountryCode.US]: 0.99,
};

export const CsvResendRequestStatusMap: Record<RequestStatus, string> = {
  [RequestStatus.PENDING]: 'Pending Resend',
  [RequestStatus.APPROVED]: 'Resend Approve',
  [RequestStatus.REJECTED]: 'Resend Rejected',
};

// ==================== FORM/DTO INTERFACES ====================

export interface ICreateUserRequest {
  username: string;
  password: string;
  email: string;
  role: Role;
  name: string;
}

export interface IUpdateUserRequest {
  username?: string;
  email?: string;
  name?: string;
  role?: Role;
}

export interface ILoginRequest {
  username: string;
  password: string;
}

export interface IAuthResponse {
  accessToken: string;
  user: IUser;
}

export interface ICreateOrderDesignPlacement {
  designId: string;
  side: Side;
  printAreaId: PrintAreaType;
  mockupUrl?: string;
}

export interface ICreateOrderDesign {
  productTemplateVariantId: string;
  quantity: number;
  placements: ICreateOrderDesignPlacement[];
  id?: string; // Optional for updates
}

export interface ICreateOrderRequest {
  orderId: string;
  trackingReference: string;
  trackingCode: string;
  designs: ICreateOrderDesign[];
  address?: string;
  platform: Platform;
  notes?: string;
  internalNotes?: string;
  orderType: OrderType;
}

export interface IUpdateOrderStatusRequest {
  status: OrderStatus;
  notes?: string;
}

export interface ICreateRefundRequestRequest {
  orderId: string;
  reason?: string;
  evidence: string;
}

export interface ICreateResendRequestRequest {
  orderId: string;
  reason?: string;
}
